/**
 * StockMovement — frontend mirror of the NestJS StockMovement model
 * (Phase 4c).
 *
 * StockMovement is an APPEND-ONLY ledger. Every quantity change to a
 * StockLot is recorded as a row here. The invariant is:
 *   SUM(movement.quantityDelta WHERE stockLotId=X) == lot.quantityOnHand
 *
 * Five movement types (mirrors backend Prisma enum):
 *
 *   receipt       Incoming from supplier — positive delta. Also used
 *                 to seed the initial quantity right after creating a
 *                 lot (which always starts at 0).
 *   consumption   Used in maintenance — negative delta. Optional
 *                 relatedWorkOrderId (Phase 4d will wire
 *                 work_order_parts properly).
 *   adjustment    Manual correction — signed non-zero. The reason
 *                 field is mandatory at service level (typically
 *                 "Inventaire physique du DD/MM/YYYY: -3 écart constat").
 *   transfer_in   Incoming from another lot — positive delta. Always
 *                 paired with a transfer_out via pairedMovementId.
 *   transfer_out  Outgoing to another lot — negative delta. Same
 *                 pairing.
 *
 * Frontend uses the type to colour-code timeline rows:
 *   incoming (receipt + transfer_in)        → emerald
 *   outgoing (consumption + transfer_out)   → red
 *   adjustment                              → amber
 */
export const STOCK_MOVEMENT_TYPES = [
  'receipt',
  'consumption',
  'adjustment',
  'transfer_in',
  'transfer_out',
] as const;
export type StockMovementType = (typeof STOCK_MOVEMENT_TYPES)[number];

export interface StockMovement {
  id: string;
  stockLotId: string;
  type: StockMovementType;
  /** Signed: positive for receipt/transfer_in, negative for the others. */
  quantityDelta: number;
  /** Snapshot of stockLot.quantityOnHand RIGHT AFTER this movement was applied. */
  quantityAfter: number;
  reason: string | null;
  /** UUID of the paired transfer_in or transfer_out movement, when applicable. */
  pairedMovementId: string | null;
  /** Optional WO link for consumption movements (Phase 4d). */
  relatedWorkOrderId: string | null;
  /** Optional batch link (Phase 4d serialised tracking). */
  partBatchId: string | null;
  createdAt: string;
  createdByUserId: string;
}

/**
 * Group movements into 3 visual buckets for colour-coding the timeline.
 *
 *   incoming   delta > 0 (receipt, transfer_in) — emerald
 *   outgoing   delta < 0 (consumption, transfer_out) — red
 *   adjustment signed (positive OR negative correction) — amber
 *
 * Adjustments are visually distinguished from receipts/consumptions
 * because they reflect a CORRECTION (audit-relevant), not a normal flow.
 */
export type StockMovementVisualKind = 'incoming' | 'outgoing' | 'adjustment';

export function visualKindOf(m: StockMovement): StockMovementVisualKind {
  if (m.type === 'adjustment') return 'adjustment';
  if (m.type === 'receipt' || m.type === 'transfer_in') return 'incoming';
  return 'outgoing';
}
